import React from "react";
import { Palette } from "../holidayConfig";
import { mix } from "./kit";
import { range } from "./sceneUtils";
import { Blush, Contact, Eye, Shaded, ShadeColors, Sphere, useToonId } from "./toon";

/*
 * Halloweens figurer: lyktgubbar, spöken, fladdermöss, en svart katt,
 * gravstenar, höbalar och en spindel. Samma tecknade teknik som julen.
 */

const STEM: ShadeColors = { base: "#6b5a24", shade: "#3d3212", light: "#a38f45" };
const NIGHT_FUR: ShadeColors = { base: "#2a1c18", shade: "#0e0806", light: "#5a443c", rim: "#6a3a22" };

/* ------------------------------------------------------------------ */
/* Pumpa / lyktgubbe                                                    */
/* ------------------------------------------------------------------ */
export const Pumpkin: React.FC<{
  x: number;
  y: number;
  s?: number;
  p: Palette;
  face?: "happy" | "spooky" | "none";
  delay?: number;
}> = ({ x, y, s = 1, p, face = "happy", delay = 0 }) => {
  const id = useToonId("pk");
  const side: ShadeColors = { base: p[600], shade: p[800], light: p[400], rim: p[700] };
  const mid: ShadeColors = { base: p[500], shade: p[700], light: p[300], rim: p[600] };
  const rib = (cx: number, rx: number, c: ShadeColors) => (
    <Shaded
      d={`M${cx - rx} -44 C ${cx - rx} -74, ${cx + rx} -74, ${cx + rx} -44 C ${cx + rx} -12, ${cx - rx} -12, ${cx - rx} -44 Z`}
      c={c}
      depth={rx * 0.35}
      highlight={{ cx: cx - rx * 0.35, cy: -60, rx: rx * 0.22, ry: 9, rot: -10, opacity: 0.55 }}
    />
  );
  const faces = {
    happy: [
      "M-30 -52 L -18 -66 L -8 -50 Q -19 -46 -30 -52 Z",
      "M8 -50 L 18 -66 L 30 -52 Q 19 -46 8 -50 Z",
      "M-5 -44 L 0 -52 L 5 -44 Z",
      "M-32 -36 Q 0 -8 32 -36 Q 26 -30 20 -31 L 16 -25 L 10 -30 Q 0 -27 -10 -30 L -16 -25 L -20 -31 Q -26 -30 -32 -36 Z",
    ],
    spooky: [
      "M-30 -48 L -14 -64 L -10 -46 Z",
      "M10 -46 L 14 -64 L 30 -48 Z",
      "M-4 -42 L 0 -50 L 4 -42 Z",
      "M-30 -34 L -22 -26 L -14 -32 L -6 -22 L 0 -30 L 6 -22 L 14 -32 L 22 -26 L 30 -34 Q 20 -14 0 -14 Q -20 -14 -30 -34 Z",
    ],
    none: [],
  }[face];
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      {face !== "none" && (
        <ellipse cx="0" cy="-12" rx="62" ry="16" fill={p[400]} opacity="0.16" filter="url(#toon-softer)" className="hol-flicker" style={{ animationDelay: `${delay}s` }} />
      )}
      <Contact cx={0} cy={-2} rx={50} ry={7} opacity={0.55} />
      {/* Stjälk */}
      <Shaded d="M-4 -70 C -6 -84, 0 -94, 10 -96 L 13 -90 C 6 -86, 4 -78, 5 -68 Z" c={STEM} depth={3} />
      <path d="M8 -90 C 18 -96, 26 -90, 22 -82 C 19 -76, 26 -72, 30 -76" stroke={STEM.base} strokeWidth="2.2" fill="none" strokeLinecap="round" />
      {rib(-30, 22, side)}
      {rib(30, 22, side)}
      {rib(-14, 22, mid)}
      {rib(14, 22, mid)}
      {rib(0, 20, { base: p[400], shade: p[600], light: p[200], rim: p[500] })}
      {faces.length > 0 && (
        <g>
          <defs>
            <radialGradient id={`${id}-in`} cx="50%" cy="60%" r="60%">
              <stop offset="0%" stopColor={p[50]} />
              <stop offset="45%" stopColor={p[200]} />
              <stop offset="100%" stopColor={p[500]} />
            </radialGradient>
          </defs>
          {/* Snittkanten (pumpans tjocklek) och ljuset innanför */}
          {faces.map((d, i) => (
            <path key={`e${i}`} d={d} fill={p[800]} />
          ))}
          <g className="hol-flicker" style={{ animationDelay: `${delay}s` }} transform="translate(-1.2 -1.5)">
            {faces.map((d, i) => (
              <path key={`g${i}`} d={d} fill={`url(#${id}-in)`} transform="scale(0.92)" style={{ transformBox: "fill-box", transformOrigin: "center" }} />
            ))}
          </g>
        </g>
      )}
    </g>
  );
};

/* ------------------------------------------------------------------ */
/* Spöke                                                                */
/* ------------------------------------------------------------------ */
export const Ghost: React.FC<{ p: Palette; mood?: "happy" | "surprised" }> = ({ p, mood = "happy" }) => {
  const c: ShadeColors = { base: "#fffaf6", shade: mix("#ffffff", p[300], 0.45), light: "#ffffff", rim: mix("#ffffff", p[200], 0.6) };
  return (
    <svg viewBox="-60 -80 120 130">
      <ellipse cx="0" cy="-10" rx="56" ry="60" fill="#ffffff" opacity="0.14" filter="url(#toon-softer)" />
      {/* Armar */}
      <Shaded d="M-38 -16 C -52 -20, -58 -8, -52 -2 C -46 2, -40 -2, -36 -6 Z" c={c} depth={4} />
      <Shaded d="M38 -18 C 50 -30, 60 -24, 56 -14 C 52 -8, 44 -8, 38 -10 Z" c={c} depth={4} />
      <Shaded
        d="M-40 26 C -44 -24, -28 -66, 0 -66 C 28 -66, 44 -24, 40 26 C 34 36, 28 22, 20 32 C 13 42, 7 26, 0 34 C -7 42, -13 26, -20 32 C -28 40, -34 26, -40 26 Z"
        c={c}
        depth={14}
        highlight={{ cx: -16, cy: -50, rx: 12, ry: 7, rot: -30, opacity: 0.9 }}
      />
      <Eye x={-13} y={-28} rx={6.5} ry={9} iris="#1f0f08" look={[0.1, 0.2]} />
      <Eye x={13} y={-28} rx={6.5} ry={9} iris="#1f0f08" look={[0.1, 0.2]} />
      <Blush x={-22} y={-14} r={6} color={p[300]} opacity={0.55} />
      <Blush x={22} y={-14} r={6} color={p[300]} opacity={0.55} />
      {mood === "happy" ? (
        <path d="M-7 -12 Q 0 -4 7 -12 Q 0 -8 -7 -12 Z" fill="#2a1208" stroke="#2a1208" strokeWidth="2" strokeLinejoin="round" />
      ) : (
        <ellipse cx="0" cy="-9" rx="4.5" ry="5.5" fill="#2a1208" />
      )}
    </svg>
  );
};

/* ------------------------------------------------------------------ */
/* Fladdermus                                                           */
/* ------------------------------------------------------------------ */
export const Bat: React.FC<{ p: Palette; speed?: number }> = ({ p, speed = 0.36 }) => {
  const body: ShadeColors = { base: "#3a2419", shade: "#140905", light: "#7a5642", rim: p[800] };
  const wing = "M-8 -4 C -18 -22, -40 -24, -52 -12 C -46 -8, -44 -2, -44 5 C -38 0, -32 2, -30 9 C -25 3, -18 3, -13 8 Z";
  return (
    <svg viewBox="-60 -34 120 60" style={{ filter: `drop-shadow(0 0 6px ${p[700]})` }}>
      <g>
        <animateTransform attributeName="transform" type="rotate" values="-18 -8 -4; 34 -8 -4; -18 -8 -4" dur={`${speed}s`} repeatCount="indefinite" />
        <path d={wing} fill="#24140d" />
        <path d="M-10 -4 L -44 -10 M -12 -2 L -34 6 M -11 0 L -24 8" stroke={p[900]} strokeWidth="1.2" opacity="0.8" />
      </g>
      <g>
        <animateTransform attributeName="transform" type="rotate" values="18 8 -4; -34 8 -4; 18 8 -4" dur={`${speed}s`} repeatCount="indefinite" />
        <path d={wing} fill="#24140d" transform="scale(-1 1)" />
        <path d="M10 -4 L 44 -10 M 12 -2 L 34 6 M 11 0 L 24 8" stroke={p[900]} strokeWidth="1.2" opacity="0.8" />
      </g>
      <path d="M-9 -12 L -11 -26 L -2 -16 Z M 9 -12 L 11 -26 L 2 -16 Z" fill="#2e1c13" />
      <path d="M-8.5 -15 L -9.5 -22 L -5 -17 Z M 8.5 -15 L 9.5 -22 L 5 -17 Z" fill={p[400]} opacity="0.6" />
      <Sphere cx={0} cy={-2} r={13} c={body} />
      <Eye x={-5} y={-5} rx={4} ry={4.8} look={[0, 0.25]} />
      <Eye x={5} y={-5} rx={4} ry={4.8} look={[0, 0.25]} />
      <path d="M-3 3 Q 0 5.5 3 3" stroke="#0a0402" strokeWidth="1.2" fill="none" strokeLinecap="round" />
      <path d="M-2.4 3.6 L -1.6 6.4 L -0.8 3.9 Z M 2.4 3.6 L 1.6 6.4 L 0.8 3.9 Z" fill="#ffffff" />
    </svg>
  );
};

/* ------------------------------------------------------------------ */
/* Svart katt                                                           */
/* ------------------------------------------------------------------ */
export const Cat: React.FC<{ x: number; y: number; s?: number; p: Palette }> = ({ x, y, s = 1, p }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    {/* Svans */}
    <path d="M18 -6 C 44 -6, 50 -30, 40 -46 C 36 -52, 30 -50, 34 -44 C 42 -30, 34 -16, 16 -16 Z" fill={NIGHT_FUR.base} />
    <Shaded d="M-24 0 C -28 -30, -20 -52, 0 -52 C 20 -52, 28 -30, 24 0 Z" c={NIGHT_FUR} depth={8} />
    {/* Huvud */}
    <Shaded d="M-22 -58 L -24 -86 L -10 -76 C -4 -78, 4 -78, 10 -76 L 24 -86 L 22 -58 C 22 -44, 12 -38, 0 -38 C -12 -38, -22 -44, -22 -58 Z" c={NIGHT_FUR} depth={6} />
    <path d="M-20 -80 L -14 -75 L -19 -70 Z M 20 -80 L 14 -75 L 19 -70 Z" fill={p[400]} opacity="0.5" />
    {/* Lysande ögon */}
    <g className="hol-breathe">
      <ellipse cx="-9" cy="-60" rx="10" ry="8" fill={p[300]} opacity="0.35" filter="url(#toon-soft)" />
      <ellipse cx="9" cy="-60" rx="10" ry="8" fill={p[300]} opacity="0.35" filter="url(#toon-soft)" />
    </g>
    <ellipse cx="-9" cy="-60" rx="6" ry="6.6" fill={p[300]} />
    <ellipse cx="9" cy="-60" rx="6" ry="6.6" fill={p[300]} />
    <ellipse cx="-9" cy="-60" rx="1.8" ry="5.2" fill="#140804" />
    <ellipse cx="9" cy="-60" rx="1.8" ry="5.2" fill="#140804" />
    <circle cx="-7" cy="-63" r="1.6" fill="#ffffff" />
    <circle cx="11" cy="-63" r="1.6" fill="#ffffff" />
    <path d="M-2.5 -51 L 2.5 -51 L 0 -48 Z" fill={p[300]} />
    <path d="M-6 -46 Q -3 -43 0 -46 Q 3 -43 6 -46" stroke="#5a4038" strokeWidth="1.2" fill="none" />
    <path d="M-8 -50 L -26 -54 M -8 -48 L -26 -46 M 8 -50 L 26 -54 M 8 -48 L 26 -46" stroke="#ffffff" strokeWidth="0.7" opacity="0.5" />
    {/* Tassar */}
    <ellipse cx="-10" cy="-1" rx="8" ry="4" fill={NIGHT_FUR.light} opacity="0.6" />
    <ellipse cx="10" cy="-1" rx="8" ry="4" fill={NIGHT_FUR.light} opacity="0.6" />
  </g>
);

/* ------------------------------------------------------------------ */
/* Gravsten, höbal, spindel                                             */
/* ------------------------------------------------------------------ */
export const Tombstone: React.FC<{ x: number; y: number; s?: number; tilt?: number; p: Palette; kind?: "round" | "cross"; text?: string }> = ({
  x,
  y,
  s = 1,
  tilt = 0,
  p,
  kind = "round",
  text = "RIP",
}) => {
  const stone: ShadeColors = { base: mix("#8b7d74", p[900], 0.35), shade: mix("#3e342f", p[950], 0.3), light: "#c9bdb3", rim: mix("#8b7d74", p[600], 0.4) };
  const d =
    kind === "round"
      ? "M-26 0 L -26 -50 C -26 -74, 26 -74, 26 -50 L 26 0 Z"
      : "M-7 0 L -7 -46 L -24 -46 L -24 -58 L -7 -58 L -7 -76 L 7 -76 L 7 -58 L 24 -58 L 24 -46 L 7 -46 L 7 0 Z";
  return (
    <g transform={`translate(${x} ${y}) rotate(${tilt}) scale(${s})`}>
      <Contact cx={4} cy={0} rx={34} ry={5} opacity={0.5} />
      <Shaded d={d} c={stone} depth={7} highlight={{ cx: -10, cy: -60, rx: 8, ry: 4, rot: -20, opacity: 0.35 }} />
      {kind === "round" && (
        <>
          <text x="0" y="-42" textAnchor="middle" fontFamily="Georgia, serif" fontWeight="700" fontSize="14" fill={stone.shade} opacity="0.9">
            {text}
          </text>
          <path d="M-14 -24 H 14 M -10 -16 H 10" stroke={stone.shade} strokeWidth="2" opacity="0.6" />
          <path d="M16 -62 L 10 -52 L 14 -46 L 8 -38" stroke={stone.shade} strokeWidth="1.2" fill="none" opacity="0.7" />
        </>
      )}
      {/* Gräs vid foten */}
      <path d="M-30 1 L -26 -9 L -23 1 L -18 -12 L -14 1 M 12 1 L 16 -10 L 19 1 L 24 -8 L 28 1" stroke="#1a0c06" strokeWidth="2.2" fill="none" strokeLinejoin="round" />
    </g>
  );
};

export const HayBale: React.FC<{ x: number; y: number; s?: number; p: Palette }> = ({ x, y, s = 1, p }) => {
  const straw: ShadeColors = { base: p[300], shade: p[600], light: p[100], rim: p[400] };
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <Contact cx={10} cy={0} rx={70} ry={8} opacity={0.5} />
      <Shaded d="M-40 0 L -40 -56 C -40 -64, 40 -64, 40 -56 L 40 0 Z" c={straw} depth={10} />
      <path d="M-40 0 L 40 0 L 40 -56 L 60 -56 L 60 0 Z" fill="none" />
      <Shaded d="M40 0 C 58 0, 64 -14, 64 -28 C 64 -44, 58 -56, 40 -56 C 22 -56, 18 -40, 18 -28 C 18 -14, 22 0, 40 0 Z" c={{ ...straw, base: p[200], shade: p[500] }} depth={6} />
      <path d="M40 -28 m -4 0 a 4 4 0 1 1 8 0 a 8 8 0 1 1 -16 0 a 12 12 0 1 1 24 0 a 16 16 0 1 1 -32 0" stroke={p[500]} strokeWidth="1.6" fill="none" opacity="0.8" />
      {range(9).map(i => (
        <path key={i} d={`M${-36 + i * 8} ${-6 - (i % 3) * 4} l ${6 + (i % 2) * 3} ${-24 - (i % 4) * 5}`} stroke={p[500]} strokeWidth="1.1" opacity="0.6" />
      ))}
      <path d="M-40 -18 H 38 M -40 -40 H 38" stroke={p[800]} strokeWidth="2.4" opacity="0.7" />
      {/* Strån som sticker ut */}
      <path d="M-34 -58 l -6 -8 M -20 -60 l 2 -10 M 4 -60 l 6 -8 M 26 -58 l 8 -6" stroke={p[300]} strokeWidth="1.5" strokeLinecap="round" />
    </g>
  );
};

export const Spider: React.FC<{ p: Palette }> = ({ p }) => {
  const body: ShadeColors = { base: "#2c1a12", shade: "#0c0503", light: "#6a4a3a", rim: p[800] };
  return (
    <svg viewBox="-30 -20 60 50">
      <g stroke="#1c100a" strokeWidth="2.2" fill="none" strokeLinecap="round">
        <path d="M-6 4 C -16 -6, -22 -4, -26 6 M -7 7 C -18 2, -24 6, -26 14 M -6 10 C -16 10, -20 16, -22 22 M -4 12 C -10 16, -12 22, -12 26" />
        <path d="M6 4 C 16 -6, 22 -4, 26 6 M 7 7 C 18 2, 24 6, 26 14 M 6 10 C 16 10, 20 16, 22 22 M 4 12 C 10 16, 12 22, 12 26" />
      </g>
      <Sphere cx={0} cy={8} r={10} c={body} />
      <Eye x={-3.6} y={6} rx={3} ry={3.6} look={[0, 0.3]} />
      <Eye x={3.6} y={6} rx={3} ry={3.6} look={[0, 0.3]} />
      <path d="M-2 12 Q 0 13.5 2 12" stroke={p[300]} strokeWidth="1" fill="none" />
    </svg>
  );
};
